import { Controller } from "@hotwired/stimulus"

// Capability selection for a new API token.
//
// The common case is "let this agent do what I can already do", so the form
// leads with presets and keeps the full grid behind the Custom option. The
// presets drive the same checkboxes the server reads, so the submitted shape is
// identical either way and the form still works with JavaScript disabled.
export default class extends Controller {
  static targets = ["checkbox", "count", "groupCount", "custom", "summary"]

  connect () {
    this.applyPreset()
  }

  get preset () {
    const checked = this.element.querySelector("[name='capability_preset']:checked")
    return checked ? checked.value : "all"
  }

  // Switching preset rewrites the selection; Custom keeps whatever the previous
  // preset produced so it opens as a starting point rather than an empty list.
  applyPreset () {
    const preset = this.preset

    if (preset === "all") {
      this.apply(this.checkboxTargets, true)
    } else if (preset === "read") {
      this.checkboxTargets.forEach((box) => {
        box.checked = box.dataset.readOnly === "true"
      })
    }

    if (this.hasCustomTarget) this.customTarget.hidden = preset !== "custom"
    this.update()
  }

  selectAll () {
    this.apply(this.checkboxTargets, true)
  }

  clearAll () {
    this.apply(this.checkboxTargets, false)
  }

  selectGroup (event) {
    this.apply(this.groupCheckboxes(event.currentTarget), true)
  }

  clearGroup (event) {
    this.apply(this.groupCheckboxes(event.currentTarget), false)
  }

  update () {
    const total = this.checkboxTargets.length
    const selected = this.checkboxTargets.filter((box) => box.checked).length

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${selected} of ${total} selected`
    }

    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = selected === total
        ? `All ${total} capabilities`
        : `${selected} of ${total} capabilities`
    }

    this.groupCountTargets.forEach((label) => {
      const boxes = this.groupCheckboxes(label)
      label.textContent = `${boxes.filter((box) => box.checked).length}/${boxes.length}`
    })
  }

  apply (checkboxes, checked) {
    checkboxes.forEach((box) => { box.checked = checked })
    this.update()
  }

  // Scope is the nearest labelled group, so the same controller serves the
  // per-group buttons and the counts without wiring ids through the markup.
  groupCheckboxes (element) {
    const group = element.closest("[data-capability-group]")
    if (!group) return []

    return this.checkboxTargets.filter((box) => group.contains(box))
  }
}
